import { useEffect, useMemo, useRef } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useQueryClient
} from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { CompanyUser, CompanyUsers } from '@/api';
import { addOrUpdateCompanyUserCache } from './company-cache';
import { FETCH_USERS_PAGINATED_QUERY_KEY } from './company-query-keys';

const PAGE_SIZE = 25;

export type FetchUsersPaginatedQuery = {
    companyId: string;
    search: string;
    teamIds?: string[];
    excludeTeamIds?: string[];
    /**
     * `false` for users in no team at all, `true` for users in at least one.
     * Omitted means no constraint.
     */
    hasTeam?: boolean;
    membership: 'all' | 'joined' | 'invited';
    roles?: ('owner' | 'admin' | 'billing' | 'member')[];
    /**
     * `admin` covers the inherited team-admin role of company owners/admins,
     * and `memberOnly` is a team entry that carries no role at all — so an
     * owner/admin never matches it.
     */
    teamRoles?: ('admin' | 'guardian' | 'memberOnly')[];
    sortBy?: 'name' | 'role' | 'teamRole' | 'teams' | 'license';
    sortOrder?: 'asc' | 'desc';
    /**
     * Defaults to true. Set false to hold off until the list is actually needed
     * — a picker with no team chosen yet would otherwise fetch the whole
     * company.
     */
    enabled?: boolean;
};

export type FetchUsersPaginatedResult = {
    users: CompanyUser[];
    total: number;
    status: 'error' | 'success' | 'pending';
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    refetch: () => void;
};

export function useFetchUsersPaginated(
    query: FetchUsersPaginatedQuery
): FetchUsersPaginatedResult {
    const {
        companyId,
        search,
        teamIds,
        excludeTeamIds,
        hasTeam,
        membership,
        roles,
        teamRoles,
        sortBy,
        sortOrder,
        enabled = true
    } = query;

    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });
    const queryClient = useQueryClient();

    const {
        data,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: [
            FETCH_USERS_PAGINATED_QUERY_KEY,
            {
                companyId,
                search,
                teamIds,
                excludeTeamIds,
                hasTeam,
                membership,
                roles,
                teamRoles,
                sortBy,
                sortOrder
            }
        ],
        queryFn: ({ pageParam }) => {
            return openApiRef.current.users
                .getUsers({
                    limit: PAGE_SIZE,
                    offset: pageParam * PAGE_SIZE,
                    companyId,
                    search: search || undefined,
                    teamIds: teamIds?.length ? teamIds : undefined,
                    excludeTeamIds: excludeTeamIds?.length
                        ? excludeTeamIds
                        : undefined,
                    hasTeam,
                    membership,
                    roles: roles?.length ? roles : undefined,
                    teamRoles: teamRoles?.length ? teamRoles : undefined,
                    sortBy,
                    sortOrder
                })
                .then((response): CompanyUsers => {
                    const users = response.data.data.users;
                    users.data.forEach((user) => {
                        addOrUpdateCompanyUserCache(queryClient, user);
                    });
                    return users;
                });
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.hasMore ? allPages.length : undefined;
        },
        // Keep showing the previous rows while a new search/filter/sort
        // combination loads, instead of flashing empty.
        placeholderData: keepPreviousData,
        enabled
    });

    // Flatten pages, deduping by id in case a mutation shifts page boundaries
    // between fetches.
    const users = useMemo(() => {
        const map = new Map<string, CompanyUser>();
        data?.pages.forEach((page) => {
            page.data.forEach((user) => {
                map.set(user.id, user);
            });
        });
        return [...map.values()];
    }, [data]);

    const total = useMemo(() => {
        const pages = data?.pages;
        return pages && pages.length > 0 ? pages[pages.length - 1].total : 0;
    }, [data]);

    return {
        users,
        total,
        status,
        fetchNextPage: () => {
            void fetchNextPage();
        },
        isFetchingNextPage,
        hasNextPage,
        refetch: () => {
            void refetch();
        }
    };
}
